import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import {
  STUDY_CHAT_API,
  STUDY_CHAT_SESSION_API,
  STUDY_CHAT_UPLOAD_API,
} from "../../../../constants/API";

/* ─────────────────────────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────────────────────────── */

interface Message {
  role: "user" | "assistant";
  content: string;
  payload?: {
    type?: string;
    markets?: string[];
    job_config_id?: string;
    [key: string]: unknown;
  };
  timestamp: string;
}

interface ProgressStep {
  step: string;
  message: string;
  status: "started" | "completed" | "error";
}

interface StudyChatProps {
  sessionId: string | null;
  onSessionCreated: (sessionId: string) => void;
  onJobComplete: (jobConfigId: string) => void;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Inline markdown renderer (bold, code, lists, line-breaks)
───────────────────────────────────────────────────────────────────────────── */

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];

  lines.forEach((line, li) => {
    // List item
    const listMatch = line.match(/^[\s]*[-*]\s+(.*)/);
    if (listMatch) {
      elements.push(
        <div key={li} className="flex gap-2 items-start my-0.5">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "var(--violet)" }} />
          <span>{inlineRender(listMatch[1])}</span>
        </div>
      );
      return;
    }
    // Numbered list
    const numMatch = line.match(/^(\d+)\.\s+(.*)/);
    if (numMatch) {
      elements.push(
        <div key={li} className="flex gap-2 items-start my-0.5">
          <span className="font-mono text-xs mt-0.5 shrink-0 w-4" style={{ color: "var(--violet)" }}>
            {numMatch[1]}.
          </span>
          <span>{inlineRender(numMatch[2])}</span>
        </div>
      );
      return;
    }
    // Heading
    const headingMatch = line.match(/^#{1,3}\s+(.*)/);
    if (headingMatch) {
      elements.push(
        <p key={li} className="font-semibold text-sm mt-2 mb-1">
          {inlineRender(headingMatch[1])}
        </p>
      );
      return;
    }
    // Normal line (empty = spacer)
    if (line.trim() === "") {
      elements.push(<div key={li} className="h-2" />);
    } else {
      elements.push(<p key={li} className="leading-relaxed">{inlineRender(line)}</p>);
    }
  });

  return <>{elements}</>;
}

function inlineRender(text: string): React.ReactNode {
  // Split on bold (**x**), italic (*x*), inline code (`x`)
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**"))
          return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
        if (part.startsWith("`") && part.endsWith("`"))
          return (
            <code
              key={i}
              className="font-mono text-[0.8em] px-1 py-0.5 rounded"
              style={{ background: "var(--violet-subtle)", color: "var(--violet)" }}
            >
              {part.slice(1, -1)}
            </code>
          );
        if (part.startsWith("*") && part.endsWith("*"))
          return <em key={i}>{part.slice(1, -1)}</em>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────────────────────────────────────── */

/** Animated typing indicator */
const TypingDots: React.FC = () => (
  <div className="flex items-center gap-1 px-4 py-3">
    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--violet)" }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
        <circle cx="12" cy="12" r="3"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
      </svg>
    </div>
    <div className="flex gap-1 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)" }}>
      {[0, 150, 300].map(delay => (
        <span
          key={delay}
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: "var(--violet)", animation: `studychat-bounce 1.2s ${delay}ms ease-in-out infinite` }}
        />
      ))}
    </div>
  </div>
);

/** Adrsta AI avatar */
const AiAvatar: React.FC = () => (
  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "var(--violet)" }}>
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
      <circle cx="12" cy="12" r="3"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
    </svg>
  </div>
);

/** Live pipeline progress shown inline while the job is running */
const JobProgressTimeline: React.FC<{
  steps: ProgressStep[];
  jobConfigId?: string;
  isComplete: boolean;
  jobError?: string | null;
}> = ({ steps, jobConfigId, isComplete, jobError }) => (
  <div className="mx-1 my-2 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm studychat-message" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)" }}>
    <p className="text-[11px] font-semibold uppercase tracking-wide mb-2.5" style={{ color: "var(--text-tertiary)" }}>
      Analysis progress
    </p>

    <div className="space-y-2">
      {steps.map((s, i) => (
        <div key={i} className={`flex items-start gap-2.5 transition-opacity duration-500 ${s.status === "completed" ? "opacity-40" : "opacity-100"}`}>
          {/* Icon */}
          {s.status === "started" && (
            <span className="mt-0.5 w-4 h-4 rounded-full border-2 border-t-transparent shrink-0"
              style={{ borderColor: "var(--violet)", borderTopColor: "transparent", animation: "studychat-spin 0.8s linear infinite" }} />
          )}
          {s.status === "completed" && (
            <span className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--success)" }}>
              <svg width="8" height="8" viewBox="0 0 12 12" fill="none" style={{ stroke: "var(--text-primary)" }} strokeWidth="2.5">
                <polyline points="2 6 5 9 10 3" />
              </svg>
            </span>
          )}
          {s.status === "error" && (
            <span className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--error)" }}>
              <svg width="8" height="8" viewBox="0 0 12 12" fill="none" stroke="#ffffff" strokeWidth="2.5">
                <line x1="3" y1="3" x2="9" y2="9"/><line x1="9" y1="3" x2="3" y2="9"/>
              </svg>
            </span>
          )}
          <p className="text-xs leading-relaxed" style={{ color: s.status === "error" ? "var(--error)" : "var(--text-secondary)" }}>
            {s.message}
          </p>
        </div>
      ))}
    </div>

    {/* Job error — user-input validation failure */}
    {jobError && (
      <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--error)" }}>
        <p className="text-xs" style={{ color: "var(--error)" }}>{jobError}</p>
      </div>
    )}

    {/* Completion CTA */}
    {isComplete && !jobError && (
      <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: "var(--success)" }}>
        <p className="text-xs font-medium" style={{ color: "var(--success)" }}>Results are ready!</p>
        <a
          href={`/experiments${jobConfigId ? `?study=${jobConfigId}` : ""}`}
          className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1 studychat-cta-btn"
          style={{ background: "var(--success)", color: "var(--text-primary)" }}
        >
          View in Experiments
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </a>
      </div>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────────────────────── */

const StudyChat: React.FC<StudyChatProps> = ({
  sessionId,
  onSessionCreated,
  onJobComplete,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [stage, setStage] = useState<string>("GREET");
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(sessionId);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [progressComplete, setProgressComplete] = useState(false);
  const [progressJobId, setProgressJobId] = useState<string | undefined>();
  const [jobError, setJobError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initialized = useRef(false);

  /* ── scroll to bottom ─────────────────────────────────────────────────── */
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, isLoading, scrollToBottom]);

  /* ── init: greeting or restore ────────────────────────────────────────── */
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (sessionId) {
      restoreSession(sessionId);
    } else {
      sendMessage("", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Socket.IO: progress, complete, error ────────────────────────────── */
  useEffect(() => {
    let socket: ReturnType<typeof import("socket.io-client").io> | null = null;
    import("socket.io-client").then(({ io }) => {
      socket = io({ path: "/socket.io", transports: ["websocket"] });

      // Step-by-step progress during job execution
      socket.on("study_progress", (data: {
        session_id: string; job_config_id: string;
        step: string; status: "started" | "completed" | "error"; message: string;
      }) => {
        if (data.session_id !== currentSessionId && data.session_id) return;
        setProgressJobId(data.job_config_id);
        setProgressSteps(prev => {
          const existing = prev.findIndex(s => s.step === data.step);
          const next = { step: data.step, message: data.message, status: data.status };
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = next;
            return updated;
          }
          return [...prev, next];
        });
      });

      // Job finished successfully
      socket.on("study_job_complete", (data: { session_id: string; job_config_id: string; message?: string }) => {
        if (data.session_id !== currentSessionId && data.session_id) return;
        setStage("COMPLETE");
        setProgressComplete(true);
        setProgressJobId(data.job_config_id);
        if (data.job_config_id) onJobComplete(data.job_config_id);
      });

      // Job failed due to user input error — surface message in chat
      socket.on("study_job_error", (data: { session_id: string; job_config_id: string; message: string }) => {
        if (data.session_id !== currentSessionId && data.session_id) return;
        setJobError(data.message);
        setStage("ERROR");
        setMessages(prev => [...prev, {
          role: "assistant",
          content: `❌ ${data.message}`,
          timestamp: new Date().toISOString(),
        }]);
      });
    }).catch(() => { /* socket not critical */ });

    return () => { socket?.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  /* ── API helpers ──────────────────────────────────────────────────────── */
  async function restoreSession(sid: string) {
    try {
      const res = await axiosInstance.get(STUDY_CHAT_SESSION_API(sid));
      const { exists, messages: hist, stage: s } = res.data;
      if (exists && hist?.length) {
        setMessages(hist.map((m: Record<string, unknown>) => ({
          role: m.role as "user" | "assistant",
          content: m.content as string,
          timestamp: m.timestamp as string ?? new Date().toISOString(),
        })));
        setStage(s ?? "GREET");
        setCurrentSessionId(sid);
      } else {
        sendMessage("", null);
      }
    } catch {
      sendMessage("", null);
    }
  }

  async function sendMessage(text: string, existingSessionId: string | null) {
    const sid = existingSessionId ?? currentSessionId;
    setIsLoading(true);

    if (text.trim()) {
      setMessages(prev => [...prev, {
        role: "user",
        content: text,
        timestamp: new Date().toISOString(),
      }]);
    }

    try {
      const res = await axiosInstance.post(STUDY_CHAT_API, {
        session_id: sid || undefined,
        message: text,
      });
      const { session_id: newSid, stage: newStage, agent_response, response_payload } = res.data;

      if (newSid && !currentSessionId) {
        setCurrentSessionId(newSid);
        onSessionCreated(newSid);
      }

      setStage(newStage);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: agent_response ?? "",
        payload: response_payload,
        timestamp: new Date().toISOString(),
      }]);

      if (newStage === "START_JOB" && response_payload?.job_config_id) {
        onJobComplete(response_payload.job_config_id as string);
      }
    } catch (err: unknown) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "❌ Something went wrong. Please try again.",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsLoading(false);
    }
  }

  async function uploadCsv(file: File) {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "❌ Please upload a CSV file (`.csv` extension).",
        timestamp: new Date().toISOString(),
      }]);
      return;
    }

    setMessages(prev => [...prev, {
      role: "user",
      content: `📎 Uploading \`${file.name}\`…`,
      timestamp: new Date().toISOString(),
    }]);
    setIsLoading(true);

    const form = new FormData();
    form.append("session_id", currentSessionId ?? "");
    form.append("file", file);

    try {
      const res = await axiosInstance.post(STUDY_CHAT_UPLOAD_API, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { stage: newStage, agent_response, response_payload } = res.data;
      setStage(newStage);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: agent_response ?? "",
        payload: response_payload,
        timestamp: new Date().toISOString(),
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "❌ Upload failed. Please check the file format and try again.",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsLoading(false);
    }
  }

  /* ── Event handlers ───────────────────────────────────────────────────── */
  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    setInputValue("");
    sendMessage(text, currentSessionId);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadCsv(file);
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadCsv(file);
  };

  const handleMarketClick = (market: string) => {
    sendMessage(market, currentSessionId);
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  const showDropZone = stage === "WAIT_CSV";

  return (
    <>
      {/* Keyframe injection */}
      <style>{`
        @keyframes studychat-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes studychat-fadein {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes studychat-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes studychat-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .studychat-message { animation: studychat-fadein 0.25s ease; }
        .studychat-textarea::placeholder { color: var(--text-tertiary); }
        .studychat-cta-btn:hover { background: var(--success) !important; opacity: 0.85; }
        .studychat-send-btn:not(:disabled):hover { background: var(--violet-hover) !important; }
        .studychat-attach-btn:hover { color: var(--violet) !important; background: var(--violet-subtle) !important; }
        .studychat-market-chip:hover { background: var(--violet-subtle) !important; }
        .studychat-dropzone:hover { background: var(--violet-subtle) !important; }
      `}</style>

      <div
        className="flex flex-col h-full border-r"
        style={{ background: "var(--bg-surface)", borderColor: "var(--border-subtle)" }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="shrink-0 px-4 py-3 border-b flex items-center gap-3" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm" style={{ background: "var(--violet)" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
              <circle cx="12" cy="12" r="3"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold leading-none" style={{ color: "var(--text-primary)" }}>
              Study Assistant
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--text-tertiary)" }}>
              Synthetic Control Setup
            </p>
          </div>
          {stage === "RUNNING" && (
            <span
              className="ml-auto flex items-center gap-1.5 text-[11px] font-medium"
              style={{ color: "var(--violet)", animation: "studychat-pulse 2s ease-in-out infinite" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--violet)" }} />
              Analyzing
            </span>
          )}
          {stage === "COMPLETE" && (
            <span className="ml-auto flex items-center gap-1.5 text-[11px] font-medium" style={{ color: "var(--success)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--success)" }} />
              Results ready
            </span>
          )}
        </div>

        {/* ── Drag-and-drop overlay ────────────────────────────────────── */}
        {isDragging && (
          <div className="absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed rounded-lg pointer-events-none" style={{ background: "var(--violet-subtle)", borderColor: "var(--violet)" }}>
            <div className="text-center">
              <div className="text-4xl mb-2">📎</div>
              <p className="font-semibold" style={{ color: "var(--violet)" }}>Drop your CSV file here</p>
              <p className="text-sm mt-1" style={{ color: "var(--violet)" }}>location · date · Y columns required</p>
            </div>
          </div>
        )}

        {/* ── Messages ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1 scroll-smooth">
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "var(--violet-subtle)" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>Starting conversation…</p>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-2 studychat-message ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
            >
              {/* Avatar */}
              {msg.role === "assistant" ? (
                <AiAvatar />
              ) : (
                <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "var(--text-disabled)" }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
              )}

              <div className={`flex flex-col gap-1 max-w-[86%] ${msg.role === "user" ? "items-end" : "items-start"}`}>
                {/* Bubble */}
                {msg.role === "assistant" ? (
                  <div
                    className="rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm text-sm"
                    style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)", borderLeft: "2px solid var(--violet)", color: "var(--text-primary)" }}
                  >
                    {renderMarkdown(msg.content)}
                  </div>
                ) : (
                  <div className="rounded-2xl rounded-tr-sm px-4 py-3 text-sm shadow-sm" style={{ background: "var(--violet)", color: "#ffffff" }}>
                    {msg.content}
                  </div>
                )}

                {/* Market chips */}
                {msg.payload?.type === "market_options" && Array.isArray(msg.payload.markets) && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(msg.payload.markets as string[]).map((m, i) => (
                      <button
                        key={i}
                        onClick={() => handleMarketClick(m)}
                        className="text-xs px-3 py-1.5 rounded-full border transition-colors font-mono studychat-market-chip"
                        style={{ borderColor: "var(--violet)", color: "var(--violet)" }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                )}

                {/* Timestamp */}
                <span className="text-[10px] px-1" style={{ color: "var(--text-tertiary)" }}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isLoading && <TypingDots />}

          {/* Pipeline progress timeline (shown once job is launched) */}
          {(stage === "RUNNING" || stage === "COMPLETE" || stage === "ERROR") && !isLoading && progressSteps.length > 0 && (
            <JobProgressTimeline
              steps={progressSteps}
              jobConfigId={progressJobId}
              isComplete={progressComplete}
              jobError={jobError}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Upload drop zone (active in WAIT_CSV stage) ───────────────── */}
        {showDropZone && !isDragging && (
          <div
            className="mx-3 mb-2 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors studychat-dropzone"
            style={{ borderColor: "var(--violet)" }}
            onClick={() => fileInputRef.current?.click()}
          >
            <p className="text-sm font-medium" style={{ color: "var(--violet)" }}>
              📎 Click to upload CSV  <span className="font-normal" style={{ color: "var(--text-tertiary)" }}>or drag and drop</span>
            </p>
            <p className="text-xs mt-1" style={{ color: "var(--text-tertiary)" }}>
              Required columns: <code className="font-mono">location</code>, <code className="font-mono">date</code>, <code className="font-mono">Y</code>
            </p>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* ── Input bar ────────────────────────────────────────────────── */}
        <div className="shrink-0 px-3 pb-3 pt-2 border-t" style={{ borderColor: "var(--border-subtle)" }}>
          <div
            className="flex items-end gap-2 rounded-2xl px-3 py-2 border transition-colors"
            style={{
              background: "var(--bg-hover)",
              borderColor: isDragging ? "var(--violet)" : "var(--border-default)",
            }}
          >
            {/* Attachment button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || stage === "RUNNING" || stage === "COMPLETE"}
              className="shrink-0 p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed studychat-attach-btn"
              style={{ color: "var(--text-tertiary)" }}
              title="Upload CSV file"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
              </svg>
            </button>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                stage === "RUNNING" ? "Analysis in progress…" :
                stage === "COMPLETE" ? "Results are ready on the right →" :
                stage === "WAIT_CSV" ? "Or type 'skip' to continue…" :
                "Type your message…"
              }
              disabled={isLoading || stage === "RUNNING"}
              rows={1}
              className="flex-1 resize-none bg-transparent text-sm outline-none py-1 max-h-32 overflow-y-auto studychat-textarea"
              style={{ lineHeight: "1.5", color: "var(--text-primary)" }}
              onInput={e => {
                const el = e.currentTarget;
                el.style.height = "auto";
                el.style.height = Math.min(el.scrollHeight, 128) + "px";
              }}
            />

            {/* Send button */}
            <button
              type="button"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading || stage === "RUNNING"}
              className="shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-colors disabled:cursor-not-allowed studychat-send-btn"
              style={{
                background: !inputValue.trim() || isLoading || stage === "RUNNING" ? "var(--bg-active)" : "var(--violet)",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={inputValue.trim() && !isLoading && stage !== "RUNNING" ? "#ffffff" : "var(--text-tertiary)"}
                strokeWidth="2.5"
              >
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
          <p className="text-[10px] text-center mt-1.5" style={{ color: "var(--text-tertiary)" }}>
            Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      </div>
    </>
  );
};

export default StudyChat;
